<template>
  <div>
    <n-search-container
      :fields="searchFields"
      @search="search"
      @reset="reset"
    >
      <n-search-input
        ref="search"
        v-model="params"
        :fields="searchFields"
      />
    </n-search-container>

    <b-card
      no-body
      class="mb-0"
    >
      <div class="m-2">
        <b-row>
          <b-col>
            <label>{{ $t('general.entries') }}</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block ml-50 mr-1"
            >
              <template #no-options>
                {{ $t('selection.noOption') }}
              </template>
            </v-select>
          </b-col>
          <b-col
            v-if="$can('create', permissionType)"
            class="text-right"
          >
            <b-button
              variant="primary"
              @click="showModal"
            >
              {{ $t('button.create') }}
            </b-button>
          </b-col>
        </b-row>
      </div>
      <div>
        <n-table
          ref="table"
          :fields="fields"
          :items="items"
          :busy="loading"
          :current-page="params.page"
          :per-page="$store.state.pagination.perPage"
          :total="total"
          :deletable="true"
          :updatable="false"
          :type="permissionType"
          :route="route"
          @sort-changed="sortChanged"
          @destroy="destroy"
        >
          <template #cell(name)="data">
            <b-link class="link-hover" @click="goToEmployees(data.item.id)">
              {{ getNameByLang(data.item) }}
            </b-link>
          </template>
          <template #cell(workingSchedule)="data">
            {{ getWorkingScheduleTitle(data.item.workSchedule) }}
          </template>
          <template #front-button="data">
            <b-button
              v-b-tooltip.hover
              :title="$t('general.view')"
              variant="info"
              pill
              size="sm"
              @click="goToEmployees(data.item.id)"
            >
              <feather-icon icon="UsersIcon" />
            </b-button>
            <b-button
              v-b-tooltip.hover
              variant="primary"
              size="sm"
              pill
              @click="editItem(data.item)"
            >
              <feather-icon icon="EditIcon" />
            </b-button>
          </template>
        </n-table>
        <n-pagination
          class="mt-2"
          :total="total"
          :per-page="$store.state.pagination.perPage"
          :page="query.page"
          @change="list"
        />
      </div>
    </b-card>

    <b-modal
      v-model="isModalVisible"
      :title="modalTitle"
      hide-footer
      size="lg"
      no-close-on-backdrop
      @hidden="resetForm"
    >
      <validation-observer
        ref="formValidation"
        #default="{ invalid }"
      >
        <validation-provider
          v-slot="{ errors }"
          :name="$t('field.name')"
          :rules="formLocale === appLocale ? 'required|min:3|max:47' : 'min:3|max:47'"
        >
          <b-form-group :label="$t('field.name')">
            <b-row>
              <b-col>
                <b-form-input
                  v-model="form.name[formLocale]"
                  :placeholder="$t('placeholder.enterTitle')"
                  :state="errors.length > 0 ? false : null"
                />
              </b-col>
              <b-col
                cols="1"
                class="d-flex align-items-center justify-content-end ml-1"
              >
                <b-form-select
                  v-model="formLocale"
                  :options="locales"
                  :style="{
                    minWidth: '65px',
                    width: 'auto',
                    maxWidth: '120px'
                  }"
                />
              </b-col>
            </b-row>
            <small
              class="text-danger"
              :style="{ paddingTop: '5px' }"
            >
              {{ formatErrorMessage(errors[0]) }}
            </small>
          </b-form-group>
        </validation-provider>

        <validation-provider
          v-slot="{ errors }"
          :name="$t('breadcrumb.workingSchedule')"
        >
          <b-form-group
            :label="$t('breadcrumb.workingSchedule')"
            label-for="workingSchedule"
          >
            <n-async-single-select
              v-model="form.workScheduleId"
              repository="workingScheduleTemplate"
              selection-key="id"
              selection-label="translations"
              :clearable="true"
              :state="errors.length > 0 ? false : null"
            />
            <small class="text-danger">{{ errors[0] }}</small>
          </b-form-group>
        </validation-provider>

        <validation-provider
          v-slot="{ errors }"
          :name="$t('field.description')"
        >
          <b-form-group
            :label="$t('field.description')"
            label-for="description"
          >
            <b-form-textarea
              id="description"
              v-model="form.description"
              rows="3"
              :state="errors.length > 0 ? false : null"
            />
            <small class="text-danger">{{ errors[0] }}</small>
          </b-form-group>
        </validation-provider>

        <b-row class="mt-3">
          <b-col class="text-right">
            <b-button
              variant="outline-secondary"
              class="mr-2"
              @click="isModalVisible = false"
            >
              {{ $t('button.cancel') }}
            </b-button>
            <b-button
              variant="primary"
              :disabled="invalid || submitting"
              @click="onSubmit"
            >
              <span
                v-if="submitting"
                class="spinner-border spinner-border-sm mr-1"
              />
              {{ isEditing ? $t('button.update') : $t('button.save') }}
            </b-button>
          </b-col>
        </b-row>
      </validation-observer>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BModal,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormSelect,
  BLink,
} from 'bootstrap-vue'
import NAsyncSingleSelect from '@/components/NAsyncSingleSelect.vue'
import Repository from '@/repositories/RepositoryFactory'
import NPagination from '@/components/NPagination.vue'
import NTable from '@/components/NTable.vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import NSearchContainer from '@/components/NSearchContainer.vue'
import NSearchInput from '@/components/NSearchInput.vue'
import VSelect from 'vue-select'

const TeamRepository = Repository.get('team')

export default {
  name: 'TeamList',
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BModal,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    NAsyncSingleSelect,
    NPagination,
    NTable,
    ValidationProvider,
    ValidationObserver,
    NSearchContainer,
    NSearchInput,
    BFormSelect,
    BLink,
    VSelect,
  },
  data() {
    return {
      items: [],
      loading: false,
      total: 0,
      perPageOptions: this.$store.state.pagination.perPageOptions,
      formLocale: this.$i18n.locale.split('-')[0],
      params: {
        name: this.$route.query.name || null,
      },
      query: {
        page: Number(this.$route.query.page) || 1,
        order: this.$route.query.order || null,
        sort: this.$route.query.sort || null,
        name: this.$route.query.name || null,
      },
      isModalVisible: false,
      isEditing: false,
      form: {
        name: {
          en: '',
          zh: '',
          km: '',
          vi: '',
        },
        workScheduleId: null,
        description: '',
      },
      selectedItem: null,
      submitting: false,
      locales: [
        { value: 'en', text: 'English' },
        { value: 'zh', text: '中文' },
        { value: 'km', text: 'ខ្មែរ' },
        { value: 'vi', text: 'Tiếng Anh' },
      ],
    }
  },
  computed: {
    modalTitle() {
      return this.isEditing
        ? this.$t('breadcrumb.updateTeam')
        : this.$t('breadcrumb.createTeam')
    },
    perPage: {
      get() {
        return this.$store.state.pagination.perPage
      },
      set(value) {
        this.$store.commit('pagination/SET_PER_PAGE', value)
      },
    },
    appLocale() {
      return this.$i18n.locale.split('-')[0]
    },
  },
  watch: {
    perPage() {
      this.list(1)
    },
  },
  mounted() {
    this.getData()
  },
  methods: {
    updateQueryString() {
      this.$router.replace({ query: this.query })
    },
    list(page = 1) {
      this.query.page = page
      this.updateQueryString()
      this.getData()
    },
    sortChanged(sortBy, sortDirection) {
      this.query.order = sortBy
      this.query.sort = sortDirection
      this.updateQueryString()
      this.getData()
    },
    getData() {
      this.loading = true
      this.query.departmentId = this.$route.params.departmentId
      TeamRepository.list(this.query)
        .then(response => {
          const { list } = response.data.data
          this.items = [...list]
          this.total = response.data.data.total
        })
        .catch(() => {})
        .finally(() => {
          this.loading = false
        })
    },
    showModal() {
      this.isModalVisible = true
      this.isEditing = false
    },
    editItem(item) {
      this.selectedItem = {
        id: item.id,
        name: {
          en: item.name?.en || '',
          zh: item.name?.zh || '',
          km: item.name?.km || '',
          vi: item.name?.vi || '',
        },
        workScheduleId: item?.workSchedule?.id,
        description: item.description,
      }
      this.form = {
        name: {
          en: item.name?.en || '',
          zh: item.name?.zh || '',
          km: item.name?.km || '',
          vi: item.name?.vi || '',
        },
        workScheduleId: item?.workSchedule?.id,
        description: item.description,
      }
      this.isEditing = true
      this.isModalVisible = true
    },
    destroy(id) {
      TeamRepository.destroy(id)
        .then(() => {
          this.getData()
        })
        .catch(() => {})
        .finally(() => {
          this.loading = false
        })
    },
    async onSubmit() {
      const isValid = await this.$refs.formValidation.validate()
      if (!isValid || this.submitting) return

      this.submitting = true
      const payload = {
        ...this.form,
        departmentId: this.$route.params.departmentId,
      }

      try {
        if (this.isEditing) {
          await TeamRepository.update(this.selectedItem.id, payload)
        } else {
          await TeamRepository.create(payload)
        }
        this.getData()
        this.isModalVisible = false
      } catch (error) {
        if (error.response?.status === 422) {
          this.$refs.formValidation.setErrors(error.response.data.message)
        }
      } finally {
        this.submitting = false
      }
    },
    resetForm() {
      this.form = {
        name: {
          en: '',
          zh: '',
          km: '',
          vi: '',
        },
        workScheduleId: null,
        description: '',
      }
      this.selectedItem = null
      if (this.$refs.formValidation) {
        this.$refs.formValidation.reset()
      }
    },
    search() {
      this.query = {
        ...this.query,
        ...this.params,
      }
      this.updateQueryString()
      this.getData()
    },
    reset() {
      this.$refs.search.reset()
      this.query = {
        ...this.query,
        ...this.params,
      }
      this.updateQueryString()
      this.getData()
    },
    formatErrorMessage(message) {
      return message ? this.$t(message) : ''
    },
    getNameByLang(data) {
      if (!data.name) return ''
      return data?.name?.[this.appLocale] || data?.name?.en || data?.name?.zh || data?.name?.km || data?.name?.vi || '-'
    },
    goToEmployees(teamId) {
      this.$router.push({
        name: 'list-team-employee',
        params: {
          departmentId: this.$route.params.departmentId,
          teamId,
        },
      })
    },
    getWorkingScheduleTitle(workingSchedule) {
      const title =  workingSchedule?.title;
      if (typeof title === 'object') {
        return title?.[this.appLocale] || title?.en || title?.zh || title?.km || title?.vi || '-';
      }
      return title || '-';
    },
  },
  setup() {
    const searchFields = [
      {
        key: 'name',
        label: 'field.team_name',
        type: 'text',
        sm: 12,
        md: 12,
        lg: 6,
      },
    ]

    const fields = [
      {
        key: 'name',
        label: 'field.team_name',
        sortable: true,
      },
      {
        key: 'workingSchedule',
        label: 'breadcrumb.workingSchedule',
        sortable: true,
      },
      {
        key: 'description',
        label: 'field.description',
      },
      {
        key: 'actions',
        label: 'general.action',
      },
    ]

    const permissionType = 'manage-time-shift'
    const route = 'team'

    return {
      permissionType,
      route,
      fields,
      searchFields,
    }
  },
}
</script>

<style lang="scss">
  .link-hover:hover {
    text-decoration: underline;
  }
</style>
